import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

export const limitsSchema = z.tuple([z.tuple([z.number(), z.number()])]);

export type Limits = z.infer<typeof limitsSchema>;

function getLimitsUrl(layer: string, bbox: string) {
  return combineUrlAndParams("/wms/", {
    service: "wms",
    request: "getlimits",
    version: "1.1.1",
    layers: layer,
    width: 16,
    height: 16,
    srs: "EPSG:4326",
    bbox,
  });
}

export function useWmsGetLimits(layer: string, bbox: string) {
  const url = getLimitsUrl(layer, bbox);

  return useParsedQuery<Limits>(
    ["lizard-api-v4", "wms", url],
    () => fetchWithError(url),
    (json: unknown) => limitsSchema.parse(json)
  );
}

// Use this outside components, so the fetched WMS limits can still
// be stored in cache.
export async function fetchAndCacheWmsGetLimits(
  queryClient: QueryClient,
  layer: string,
  bbox: string
): Promise<Limits | null> {
  const url = getLimitsUrl(layer, bbox);
  try {
    const response = await fetchWithError(url);
    const limits = limitsSchema.parse(response);

    queryClient.setQueryData(["lizard-api-v4", "wms", url], limits);
    return limits;
  } catch (e) {
    return null;
  }
}
